import React__default, { useMemo } from 'react';
import styled from 'styled-components';
import sx from '../../sx.js';
import { themeGet } from '../../utils/theme-get.js';
import Box from '../Box/Box.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const ConnectorWrapper = styled(Box).withConfig({
  displayName: "RuleStepConnector__ConnectorWrapper",
  componentId: "sc-1ufs6xd-0"
})(["position:relative;transition-property:border-color;transition-duration:normal;", ""], sx);
const StyledLine = styled(Box).withConfig({
  displayName: "RuleStepConnector__StyledLine",
  componentId: "sc-1ufs6xd-1"
})(["border-left-width:", ";border-left-color:", ";border-left-style:", ";"], p => p.isSelectedStep ? '1.3px' : '1px', p => {
  if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected');
  if (p.isLastStep) return 'transparent';
  return themeGet('colors.border.accent.gray');
}, p => !p.isLastStep ? 'dashed' : '');
const ArrowWrapper = styled(Box).withConfig({
  displayName: "RuleStepConnector__ArrowWrapper",
  componentId: "sc-1ufs6xd-2"
})(["position:absolute;&[data-direction='right']{transform:rotate(90deg);bottom:0;top:100%;left:15px;}&[data-direction='left']{transform:rotate(-90deg);bottom:0;top:100%;left:-15px;}&[data-direction='down']{bottom:0;top:calc(100% + 12px);transform:rotate(180deg);}"]);
/* the connector arrow line itself */
const Line = styled(Box).withConfig({
  displayName: "RuleStepConnector__Line",
  componentId: "sc-1ufs6xd-3"
})(["border-right-width:", ";border-right-style:dashed;border-color:", ";display:inline-block;&[data-direction='right']{height:3px;}&[data-direction='left']{height:3px;}&[data-direction='down']{height:2px;}"], p => p.isSelectedStep ? '1.3px' : '1px', p => {
  if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected');
  if (p.isLastStep) return 'transparent';
  return themeGet('colors.border.accent.gray');
});
/* the connector arrow tip */
const TipRight = styled(Box).withConfig({
  displayName: "RuleStepConnector__TipRight",
  componentId: "sc-1ufs6xd-4"
})(["position:absolute;top:5px;left:2px;height:6px;border-right-width:", ";border-right-style:dashed;border-color:", ";display:inline-block;transform:rotate(-45deg);&[data-direction='right']{bottom:0;top:5px;}&[data-direction='left']{bottom:0;top:5px;}&[data-direction='down']{bottom:0;top:0;}"], p => p.isSelectedStep ? '1.3px' : '1px', p => {
  if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected');
  if (p.isLastStep) return 'transparent';
  return themeGet('colors.border.accent.gray');
});
const TipLeft = styled(Box).withConfig({
  displayName: "RuleStepConnector__TipLeft",
  componentId: "sc-1ufs6xd-5"
})(["position:absolute;top:5px;right:2px;height:6px;border-right-width:", ";border-right-style:dashed;border-color:", ";display:inline-block;transform:rotate(45deg);&[data-direction='right']{bottom:-6px;top:5px;}&[data-direction='left']{bottom:0;top:5px;}&[data-direction='down']{bottom:0;top:0;}"], p => p.isSelectedStep ? '1.3px' : '1px', p => {
  if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected');
  if (p.isLastStep) return 'transparent';
  return themeGet('colors.border.accent.gray');
});
const AngleArrow = ({
  direction,
  index,
  isCompletedStep,
  isSelectedStep,
  isLastStep,
  testId,
  ...props
}) => {
  const angleArrowLineTestId = `${testId}-lozenge`;
  return /*#__PURE__*/React__default.createElement(ArrowWrapper, {
    as: "div",
    index: index,
    isSelectedStep: isSelectedStep,
    "data-direction": direction,
    "data-testid": angleArrowLineTestId
  }, /*#__PURE__*/React__default.createElement(TipLeft, {
    as: "span",
    index: index,
    isSelectedStep: isSelectedStep,
    isCompletedStep: isCompletedStep
  }), /*#__PURE__*/React__default.createElement(Line, _extends({
    as: "span",
    index: index,
    "data-direction": direction,
    isSelectedStep: isSelectedStep,
    isCompletedStep: isCompletedStep,
    isLastStep: isLastStep
  }, props)), /*#__PURE__*/React__default.createElement(TipRight, {
    as: "span",
    index: index,
    isSelectedStep: isSelectedStep,
    isCompletedStep: isCompletedStep
  }));
};
AngleArrow.displayName = "AngleArrow";
const RuleStepConnector = ({
  isLastStep,
  isSelectedStep: isSelectedProp,
  isCompletedStep,
  children,
  direction = 'right',
  index,
  testId,
  ...props
}) => {
  const isActive = isSelectedProp;
  useMemo(() => {
    const base = {
      marginBottom: 2,
      marginTop: 2,
      paddingBottom: 4,
      marginRight: 0,
      height: 'auto',
      alignSelf: 'stretch',
      borderTopWidth: 0,
      borderInlineStartWidth: isLastStep ? 0 : '2px',
      minHeight: isLastStep ? 'auto' : '1.5rem',
      borderLeftWidth: isLastStep ? 0 : '2px',
      borderLeftStyle: 'solid',
      marginLeft: '18px'
    };
    return base;
  }, [isLastStep]);
  const lineTestId = `${testId}-lozenge`;
  const arrowTestId = `${testId}-arrow`;
  return /*#__PURE__*/React__default.createElement(ConnectorWrapper, _extends({
    "data-highlighted": Boolean(isActive),
    "data-testid": lineTestId
  }, props), isLastStep ? /*#__PURE__*/React__default.createElement(StyledLine, {
    index: index,
    isSelectedStep: isActive,
    isLastStep: isLastStep,
    "data-direction": direction
  }, children) : /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(StyledLine, {
    index: index,
    isSelectedStep: isActive,
    isCompletedStep: isCompletedStep,
    "data-direction": direction
  }, children), /*#__PURE__*/React__default.createElement(AngleArrow, {
    index: index,
    direction: direction,
    isSelectedStep: isActive,
    isCompletedStep: isCompletedStep,
    testId: arrowTestId
  })));
};
RuleStepConnector.displayName = "RuleStepConnector";
RuleStepConnector.displayName = 'RuleStepConnector';
var RuleStepConnector$1 = RuleStepConnector;

export { RuleStepConnector$1 as default };
